import React, { useEffect, useMemo, useState } from "react";
import {
  FaCalendarAlt,
  FaClock,
  FaPlus,
  FaTrash,
  FaUserMd,
} from "react-icons/fa";
import StaffLayout from "./StaffLayout";
import { staffAPI } from "../../services/api";
import { handleApiError } from "../../utils/errorUtils";
import "./ProviderAvailabilityPage.css";

const DAY_OPTIONS = [
  { dayOfWeek: 0, label: "Sunday" },
  { dayOfWeek: 1, label: "Monday" },
  { dayOfWeek: 2, label: "Tuesday" },
  { dayOfWeek: 3, label: "Wednesday" },
  { dayOfWeek: 4, label: "Thursday" },
  { dayOfWeek: 5, label: "Friday" },
  { dayOfWeek: 6, label: "Saturday" },
];

const createLocalId = () =>
  `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

const createWindow = (serviceCodes = []) => ({
  localId: createLocalId(),
  startTime: "09:00",
  endTime: "17:00",
  serviceCodes,
});

const createOverride = () => ({
  localId: createLocalId(),
  date: "",
  isUnavailableAllDay: false,
  reason: "",
  isActive: true,
  windows: [createWindow()],
});

const createEmptyAvailabilityRows = () =>
  DAY_OPTIONS.map(({ dayOfWeek, label }) => ({
    dayOfWeek,
    label,
    windows: [],
  }));

const todayString = new Date().toISOString().slice(0, 10);

const formatOverrideDate = (value) => {
  if (!value) {
    return "Select a date";
  }

  const date = new Date(`${value}T00:00:00`);
  return date.toLocaleDateString([], {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const getAvailabilitySourceLabel = (source) => {
  if (source === "override") {
    return "Date override";
  }

  return "Weekly schedule";
};

const ProviderAvailabilityPage = () => {
  const [providers, setProviders] = useState([]);
  const [therapistUsers, setTherapistUsers] = useState([]);
  const [selectedProviderId, setSelectedProviderId] = useState("");
  const [serviceOfferings, setServiceOfferings] = useState([]);
  const [providerForm, setProviderForm] = useState({
    name: "",
    role: "massage_therapist",
    linkedUser: "",
    active: true,
    serviceCapabilities: [],
    bio: "",
    notes: "",
  });
  const [availabilityRows, setAvailabilityRows] = useState(
    createEmptyAvailabilityRows()
  );
  const [availabilityOverrides, setAvailabilityOverrides] = useState([]);
  const [previewDate, setPreviewDate] = useState(todayString);
  const [previewServiceCode, setPreviewServiceCode] = useState("");
  const [previewResult, setPreviewResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [initializingServices, setInitializingServices] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const massageServiceOptions = useMemo(
    () =>
      serviceOfferings.filter(
        (serviceOffering) => serviceOffering.category === "massage"
      ),
    [serviceOfferings]
  );

  const resetFlashMessages = () => {
    setError("");
    setSuccess("");
  };

  const mapAvailabilityRows = (availability = []) => {
    const groupedByDay = new Map(
      DAY_OPTIONS.map(({ dayOfWeek }) => [dayOfWeek, []])
    );

    availability.forEach((entry) => {
      if (!groupedByDay.has(entry.dayOfWeek)) {
        groupedByDay.set(entry.dayOfWeek, []);
      }

      groupedByDay.get(entry.dayOfWeek).push({
        localId: createLocalId(),
        startTime: entry.startTime || "09:00",
        endTime: entry.endTime || "17:00",
        serviceCodes: entry.serviceCodes || [],
      });
    });

    return DAY_OPTIONS.map(({ dayOfWeek, label }) => ({
      dayOfWeek,
      label,
      windows: (groupedByDay.get(dayOfWeek) || []).sort((left, right) =>
        left.startTime.localeCompare(right.startTime)
      ),
    }));
  };

  const mapOverrideRows = (overrides = []) =>
    overrides
      .map((override) => ({
        localId: createLocalId(),
        date: override.date
          ? new Date(override.date).toISOString().slice(0, 10)
          : "",
        isUnavailableAllDay: Boolean(override.isUnavailableAllDay),
        reason: override.reason || "",
        isActive: override.isActive !== false,
        windows: Array.isArray(override.windows) && override.windows.length
          ? override.windows.map((windowEntry) => ({
              localId: createLocalId(),
              startTime: windowEntry.startTime || "09:00",
              endTime: windowEntry.endTime || "17:00",
              serviceCodes: windowEntry.serviceCodes || [],
            }))
          : [createWindow()],
      }))
      .sort((left, right) => left.date.localeCompare(right.date));

  const loadProvidersAndServices = async (preferredProviderId = "") => {
    try {
      setLoading(true);
      const [providersResponse, servicesResponse, therapistUsersResponse] =
        await Promise.all([
        staffAPI.getProviders(),
        staffAPI.getProviderServiceOfferings(),
        staffAPI.getTherapistUsers(),
        ]);

      const loadedProviders = providersResponse.data.providers || [];
      const loadedServices = servicesResponse.data.serviceOfferings || [];
      const loadedTherapistUsers =
        therapistUsersResponse.data.therapistUsers || [];

      setProviders(loadedProviders);
      setServiceOfferings(loadedServices);
      setTherapistUsers(loadedTherapistUsers);

      const defaultServiceCode =
        loadedServices.find((service) => service.category === "massage")?.code ||
        "";
      setPreviewServiceCode((currentValue) => currentValue || defaultServiceCode);

      const nextSelectedProviderId =
        preferredProviderId ||
        selectedProviderId ||
        (loadedProviders.length ? loadedProviders[0]._id : "");
      setSelectedProviderId(nextSelectedProviderId);

      if (!loadedProviders.length) {
        setProviderForm((currentValue) => ({
          ...currentValue,
          linkedUser: "",
          serviceCapabilities:
            currentValue.serviceCapabilities.length > 0
              ? currentValue.serviceCapabilities
              : loadedServices
                  .filter((service) => service.category === "massage")
                  .map((service) => service.code),
        }));
      }
    } catch (err) {
      handleApiError(
        err,
        "ProviderAvailabilityPage.loadProvidersAndServices",
        setError,
        "Unable to load massage provider settings."
      );
    } finally {
      setLoading(false);
    }
  };

  const loadProviderDetail = async (providerId) => {
    if (!providerId) {
      setAvailabilityRows(createEmptyAvailabilityRows());
      setAvailabilityOverrides([]);
      setPreviewResult(null);
      return;
    }

    try {
      setDetailLoading(true);
      const response = await staffAPI.getProviderAvailability(providerId);
      const { provider, availability, overrides } = response.data;

      setProviderForm({
        name: provider.name || "",
        role: provider.role || "massage_therapist",
        linkedUser: provider.linkedUser?._id || "",
        active: provider.active !== false,
        serviceCapabilities: provider.serviceCapabilities || [],
        bio: provider.bio || "",
        notes: provider.notes || "",
      });
      setAvailabilityRows(mapAvailabilityRows(availability));
      setAvailabilityOverrides(mapOverrideRows(overrides));
    } catch (err) {
      handleApiError(
        err,
        "ProviderAvailabilityPage.loadProviderDetail",
        setError,
        "Unable to load provider availability."
      );
    } finally {
      setDetailLoading(false);
    }
  };

  useEffect(() => {
    loadProvidersAndServices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadProviderDetail(selectedProviderId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProviderId]);

  const handleProviderSubmit = async (event) => {
    event.preventDefault();
    resetFlashMessages();

    try {
      const payload = {
        ...providerForm,
        linkedUser: providerForm.linkedUser || null,
        serviceCapabilities: providerForm.serviceCapabilities,
      };

      if (selectedProviderId) {
        await staffAPI.updateProvider(selectedProviderId, payload);
        setSuccess("Massage therapist updated successfully.");
        await loadProvidersAndServices(selectedProviderId);
        await loadProviderDetail(selectedProviderId);
      } else {
        const response = await staffAPI.createProvider(payload);
        setSuccess("Massage therapist created successfully.");
        await loadProvidersAndServices(response.data.provider._id);
      }
    } catch (err) {
      handleApiError(
        err,
        "ProviderAvailabilityPage.handleProviderSubmit",
        setError,
        "Unable to save massage therapist details."
      );
    }
  };

  const handleAvailabilitySave = async () => {
    resetFlashMessages();

    if (!selectedProviderId) {
      setError("Create or select a massage therapist before saving availability.");
      return;
    }

    try {
      const availability = availabilityRows.flatMap((row) =>
        row.windows.map((windowEntry) => ({
          dayOfWeek: row.dayOfWeek,
          startTime: windowEntry.startTime,
          endTime: windowEntry.endTime,
          isActive: true,
          serviceCodes: windowEntry.serviceCodes,
        }))
      );

      await staffAPI.setProviderAvailability(selectedProviderId, { availability });
      setSuccess("Weekly massage availability saved successfully.");
      await loadProviderDetail(selectedProviderId);
    } catch (err) {
      handleApiError(
        err,
        "ProviderAvailabilityPage.handleAvailabilitySave",
        setError,
        "Unable to save weekly availability."
      );
    }
  };

  const handleOverridesSave = async () => {
    resetFlashMessages();

    if (!selectedProviderId) {
      setError("Create or select a massage therapist before saving overrides.");
      return;
    }

    try {
      const overrides = availabilityOverrides
        .filter((override) => override.date)
        .map((override) => ({
          date: override.date,
          isUnavailableAllDay: override.isUnavailableAllDay,
          reason: override.reason,
          isActive: override.isActive,
          windows: override.isUnavailableAllDay
            ? []
            : override.windows.map((windowEntry) => ({
                startTime: windowEntry.startTime,
                endTime: windowEntry.endTime,
                serviceCodes: windowEntry.serviceCodes,
              })),
        }));

      await staffAPI.setProviderAvailabilityOverrides(selectedProviderId, {
        overrides,
      });
      setSuccess("Date-specific massage overrides saved successfully.");
      await loadProviderDetail(selectedProviderId);
    } catch (err) {
      handleApiError(
        err,
        "ProviderAvailabilityPage.handleOverridesSave",
        setError,
        "Unable to save availability overrides."
      );
    }
  };

  const handlePreview = async () => {
    resetFlashMessages();

    if (!massageServiceOptions.length) {
      setError(
        "Initialize the default massage services before previewing massage slots."
      );
      return;
    }

    if (!selectedProviderId || !previewServiceCode || !previewDate) {
      setError("Choose a therapist, service, and date before previewing slots.");
      return;
    }

    try {
      setPreviewLoading(true);
      const response = await staffAPI.previewMassageAvailability(selectedProviderId, {
        date: previewDate,
        serviceCode: previewServiceCode,
      });
      setPreviewResult(response.data);
    } catch (err) {
      handleApiError(
        err,
        "ProviderAvailabilityPage.handlePreview",
        setError,
        "Unable to preview massage availability."
      );
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleInitializeServices = async () => {
    resetFlashMessages();

    try {
      setInitializingServices(true);
      const response = await staffAPI.initializeDefaultMassageServices();
      const providerId = response.data.provider?._id || selectedProviderId;
      setSuccess(
        "Default massage services are ready. You can now assign services and preview slots."
      );
      await loadProvidersAndServices(providerId);
      if (providerId) {
        await loadProviderDetail(providerId);
      }
    } catch (err) {
      handleApiError(
        err,
        "ProviderAvailabilityPage.handleInitializeServices",
        setError,
        "Unable to initialize the default massage services."
      );
    } finally {
      setInitializingServices(false);
    }
  };

  const toggleProviderServiceCapability = (serviceCode) => {
    setProviderForm((currentValue) => ({
      ...currentValue,
      serviceCapabilities: currentValue.serviceCapabilities.includes(serviceCode)
        ? currentValue.serviceCapabilities.filter((code) => code !== serviceCode)
        : [...currentValue.serviceCapabilities, serviceCode],
    }));
  };

  const startNewProvider = () => {
    setSelectedProviderId("");
    setPreviewResult(null);
    setProviderForm({
      name: "",
      role: "massage_therapist",
      linkedUser: "",
      active: true,
      serviceCapabilities: massageServiceOptions.map((service) => service.code),
      bio: "",
      notes: "",
    });
    setAvailabilityRows(createEmptyAvailabilityRows());
    setAvailabilityOverrides([]);
    resetFlashMessages();
  };

  const addWeeklyWindow = (dayOfWeek) => {
    setAvailabilityRows((currentRows) =>
      currentRows.map((row) =>
        row.dayOfWeek === dayOfWeek
          ? {
              ...row,
              windows: [...row.windows, createWindow()],
            }
          : row
      )
    );
  };

  const updateWeeklyWindow = (dayOfWeek, localId, field, value) => {
    setAvailabilityRows((currentRows) =>
      currentRows.map((row) =>
        row.dayOfWeek === dayOfWeek
          ? {
              ...row,
              windows: row.windows.map((windowEntry) =>
                windowEntry.localId === localId
                  ? { ...windowEntry, [field]: value }
                  : windowEntry
              ),
            }
          : row
      )
    );
  };

  const removeWeeklyWindow = (dayOfWeek, localId) => {
    setAvailabilityRows((currentRows) =>
      currentRows.map((row) =>
        row.dayOfWeek === dayOfWeek
          ? {
              ...row,
              windows: row.windows.filter(
                (windowEntry) => windowEntry.localId !== localId
              ),
            }
          : row
      )
    );
  };

  const toggleWeeklyWindowService = (dayOfWeek, localId, serviceCode) => {
    setAvailabilityRows((currentRows) =>
      currentRows.map((row) =>
        row.dayOfWeek === dayOfWeek
          ? {
              ...row,
              windows: row.windows.map((windowEntry) => {
                if (windowEntry.localId !== localId) {
                  return windowEntry;
                }

                const serviceCodes = windowEntry.serviceCodes.includes(serviceCode)
                  ? windowEntry.serviceCodes.filter((code) => code !== serviceCode)
                  : [...windowEntry.serviceCodes, serviceCode];

                return { ...windowEntry, serviceCodes };
              }),
            }
          : row
      )
    );
  };

  const addOverride = () => {
    setAvailabilityOverrides((currentOverrides) => [
      ...currentOverrides,
      createOverride(),
    ]);
  };

  const updateOverride = (localId, field, value) => {
    setAvailabilityOverrides((currentOverrides) =>
      currentOverrides.map((override) =>
        override.localId === localId
          ? {
              ...override,
              [field]: value,
              windows:
                field === "isUnavailableAllDay" && value
                  ? []
                  : field === "isUnavailableAllDay" &&
                    !value &&
                    !override.windows.length
                  ? [createWindow()]
                  : override.windows,
            }
          : override
      )
    );
  };

  const removeOverride = (localId) => {
    setAvailabilityOverrides((currentOverrides) =>
      currentOverrides.filter((override) => override.localId !== localId)
    );
  };

  const addOverrideWindow = (localId) => {
    setAvailabilityOverrides((currentOverrides) =>
      currentOverrides.map((override) =>
        override.localId === localId
          ? {
              ...override,
              windows: [...override.windows, createWindow()],
            }
          : override
      )
    );
  };

  const updateOverrideWindow = (overrideId, windowId, field, value) => {
    setAvailabilityOverrides((currentOverrides) =>
      currentOverrides.map((override) =>
        override.localId === overrideId
          ? {
              ...override,
              windows: override.windows.map((windowEntry) =>
                windowEntry.localId === windowId
                  ? { ...windowEntry, [field]: value }
                  : windowEntry
              ),
            }
          : override
      )
    );
  };

  const removeOverrideWindow = (overrideId, windowId) => {
    setAvailabilityOverrides((currentOverrides) =>
      currentOverrides.map((override) =>
        override.localId === overrideId
          ? {
              ...override,
              windows: override.windows.filter(
                (windowEntry) => windowEntry.localId !== windowId
              ),
            }
          : override
      )
    );
  };

  const toggleOverrideWindowService = (overrideId, windowId, serviceCode) => {
    setAvailabilityOverrides((currentOverrides) =>
      currentOverrides.map((override) =>
        override.localId === overrideId
          ? {
              ...override,
              windows: override.windows.map((windowEntry) => {
                if (windowEntry.localId !== windowId) {
                  return windowEntry;
                }

                const serviceCodes = windowEntry.serviceCodes.includes(serviceCode)
                  ? windowEntry.serviceCodes.filter((code) => code !== serviceCode)
                  : [...windowEntry.serviceCodes, serviceCode];

                return { ...windowEntry, serviceCodes };
              }),
            }
          : override
      )
    );
  };

  return (
    <StaffLayout>
      <div className="provider-availability-page">
        <header className="provider-page-header">
          <p className="provider-eyebrow">Massage Setup</p>
          <h1>Massage Availability</h1>
          <p className="provider-intro">
            Configure the therapist, weekly recurring hours, and specific-date
            overrides that drive customer-bookable massage slots in Suite #1.
          </p>
        </header>

        {error && <div className="provider-alert error">{error}</div>}
        {success && <div className="provider-alert success">{success}</div>}

        <div className="provider-layout-grid">
          <section className="provider-card">
            <div className="provider-card-header">
              <div>
                <h2>Massage therapists</h2>
                <p>Select an existing provider or create a new one.</p>
              </div>
              <button
                type="button"
                className="provider-secondary-button"
                onClick={startNewProvider}
              >
                <FaPlus />
                New therapist
              </button>
            </div>

            {loading ? (
              <p className="provider-empty-state">Loading therapist list...</p>
            ) : providers.length ? (
              <div className="provider-list">
                {providers.map((provider) => (
                  <button
                    key={provider._id}
                    type="button"
                    className={`provider-list-item ${
                      provider._id === selectedProviderId ? "selected" : ""
                    }`}
                    onClick={() => setSelectedProviderId(provider._id)}
                  >
                    <span className="provider-list-icon">
                      <FaUserMd />
                    </span>
                    <span className="provider-list-copy">
                      <strong>{provider.name}</strong>
                      <span>{provider.active ? "Active" : "Inactive"}</span>
                    </span>
                  </button>
                ))}
              </div>
            ) : (
              <p className="provider-empty-state">
                No massage therapists yet. Create the first provider to continue.
              </p>
            )}
          </section>

          <div className="provider-main-stack">
            <section className="provider-card">
              <div className="provider-card-header">
                <div>
                  <h2>Therapist details</h2>
                  <p>Start with one customer-bookable therapist for Suite #1.</p>
                </div>
              </div>

              <form className="provider-form" onSubmit={handleProviderSubmit}>
                <div className="provider-two-column">
                  <label>
                    Therapist name
                    <input
                      type="text"
                      value={providerForm.name}
                      onChange={(event) =>
                        setProviderForm((currentValue) => ({
                          ...currentValue,
                          name: event.target.value,
                        }))
                      }
                      placeholder="Massage therapist name"
                    />
                  </label>
                  <label>
                    Role
                    <input
                      type="text"
                      value={providerForm.role}
                      onChange={(event) =>
                        setProviderForm((currentValue) => ({
                          ...currentValue,
                          role: event.target.value,
                        }))
                      }
                    />
                  </label>
                </div>

                <label>
                  Linked login account
                  <select
                    value={providerForm.linkedUser}
                    onChange={(event) =>
                      setProviderForm((currentValue) => ({
                        ...currentValue,
                        linkedUser: event.target.value,
                      }))
                    }
                  >
                    <option value="">Not linked</option>
                    {therapistUsers.map((user) => (
                      <option key={user._id} value={user._id}>
                        {[
                          user.preferredName || user.firstName || "",
                          user.lastName || "",
                        ]
                          .filter(Boolean)
                          .join(" ")
                          .trim() || user.email}{" "}
                        ({user.email})
                      </option>
                    ))}
                  </select>
                  <p className="provider-field-note">
                    Link a massage therapist login here so they can access the
                    therapist portal and manage their own schedule.
                  </p>
                </label>

                <label>
                  Bio
                  <textarea
                    rows="3"
                    value={providerForm.bio}
                    onChange={(event) =>
                      setProviderForm((currentValue) => ({
                        ...currentValue,
                        bio: event.target.value,
                      }))
                    }
                    placeholder="Optional short staff-facing bio"
                  />
                </label>

                <label>
                  Notes
                  <textarea
                    rows="3"
                    value={providerForm.notes}
                    onChange={(event) =>
                      setProviderForm((currentValue) => ({
                        ...currentValue,
                        notes: event.target.value,
                      }))
                    }
                    placeholder="Optional internal notes"
                  />
                </label>

                <fieldset className="provider-fieldset">
                  <legend>Customer-bookable massage services</legend>
                  {massageServiceOptions.map((service) => (
                    <label key={service.code} className="checkbox-row">
                      <input
                        type="checkbox"
                        checked={providerForm.serviceCapabilities.includes(
                          service.code
                        )}
                        onChange={() =>
                          toggleProviderServiceCapability(service.code)
                        }
                      />
                      <span>
                        {service.name} ({service.durationMinutes} min)
                      </span>
                    </label>
                  ))}
                </fieldset>

                <label className="checkbox-row">
                  <input
                    type="checkbox"
                    checked={providerForm.active}
                    onChange={(event) =>
                      setProviderForm((currentValue) => ({
                        ...currentValue,
                        active: event.target.checked,
                      }))
                    }
                  />
                  <span>Therapist is active and bookable</span>
                </label>

                <button type="submit" className="provider-primary-button">
                  {selectedProviderId ? "Save therapist" : "Create therapist"}
                </button>
              </form>
            </section>

            {!massageServiceOptions.length && (
              <section className="provider-card provider-setup-card">
                <div className="provider-card-header">
                  <div>
                    <h2>Initialize massage services</h2>
                    <p>
                      The default 60 and 90 minute massage offerings have not
                      been created yet, so therapist services and slot previews
                      are unavailable.
                    </p>
                  </div>
                </div>

                <button
                  type="button"
                  className="provider-primary-button"
                  onClick={handleInitializeServices}
                  disabled={initializingServices}
                >
                  <FaPlus />
                  {initializingServices
                    ? "Initializing services..."
                    : "Initialize default massage services"}
                </button>
              </section>
            )}

            <section className="provider-card">
              <div className="provider-card-header">
                <div>
                  <h2>Weekly recurring hours</h2>
                  <p>
                    Add as many windows per day as needed. Leaving service chips
                    unselected makes that window available for both massage
                    lengths.
                  </p>
                </div>
                {detailLoading && (
                  <span className="provider-loading-label">Loading...</span>
                )}
              </div>

              <div className="provider-availability-grid">
                {availabilityRows.map((row) => (
                  <article key={row.dayOfWeek} className="provider-availability-row">
                    <div className="provider-day-header provider-day-header-wide">
                      <div>
                        <strong>{row.label}</strong>
                        <p>
                          {row.windows.length
                            ? `${row.windows.length} window${
                                row.windows.length === 1 ? "" : "s"
                              }`
                            : "No recurring availability"}
                        </p>
                      </div>
                      <button
                        type="button"
                        className="provider-secondary-button provider-inline-button"
                        onClick={() => addWeeklyWindow(row.dayOfWeek)}
                      >
                        <FaPlus />
                        Add window
                      </button>
                    </div>

                    {row.windows.length ? (
                      <div className="provider-window-stack">
                        {row.windows.map((windowEntry, index) => (
                          <div
                            key={windowEntry.localId}
                            className="provider-window-card"
                          >
                            <div className="provider-window-header">
                              <strong>Window {index + 1}</strong>
                              <button
                                type="button"
                                className="provider-icon-button"
                                onClick={() =>
                                  removeWeeklyWindow(
                                    row.dayOfWeek,
                                    windowEntry.localId
                                  )
                                }
                              >
                                <FaTrash />
                              </button>
                            </div>

                            <div className="provider-time-row">
                              <label>
                                Start
                                <input
                                  type="time"
                                  value={windowEntry.startTime}
                                  onChange={(event) =>
                                    updateWeeklyWindow(
                                      row.dayOfWeek,
                                      windowEntry.localId,
                                      "startTime",
                                      event.target.value
                                    )
                                  }
                                />
                              </label>
                              <label>
                                End
                                <input
                                  type="time"
                                  value={windowEntry.endTime}
                                  onChange={(event) =>
                                    updateWeeklyWindow(
                                      row.dayOfWeek,
                                      windowEntry.localId,
                                      "endTime",
                                      event.target.value
                                    )
                                  }
                                />
                              </label>
                            </div>

                            <div className="provider-service-chip-row">
                              {massageServiceOptions.map((service) => (
                                <button
                                  key={`${windowEntry.localId}-${service.code}`}
                                  type="button"
                                  className={`provider-service-chip ${
                                    windowEntry.serviceCodes.includes(service.code)
                                      ? "selected"
                                      : ""
                                  }`}
                                  onClick={() =>
                                    toggleWeeklyWindowService(
                                      row.dayOfWeek,
                                      windowEntry.localId,
                                      service.code
                                    )
                                  }
                                >
                                  {service.name}
                                </button>
                              ))}
                            </div>
                            <p className="provider-field-note">
                              No selected services means this window supports both
                              massage lengths.
                            </p>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="provider-empty-state">
                        No recurring windows yet for {row.label}.
                      </p>
                    )}
                  </article>
                ))}
              </div>

              <button
                type="button"
                className="provider-primary-button"
                onClick={handleAvailabilitySave}
              >
                Save weekly availability
              </button>
            </section>

            <section className="provider-card">
              <div className="provider-card-header">
                <div>
                  <h2>Date-specific overrides</h2>
                  <p>
                    Use overrides for vacations, shorter days, special extended
                    hours, or one-off blackout dates.
                  </p>
                </div>
                <button
                  type="button"
                  className="provider-secondary-button"
                  onClick={addOverride}
                >
                  <FaPlus />
                  Add override
                </button>
              </div>

              {availabilityOverrides.length ? (
                <div className="provider-window-stack">
                  {availabilityOverrides.map((override, index) => (
                    <article
                      key={override.localId}
                      className="provider-window-card provider-override-card"
                    >
                      <div className="provider-window-header">
                        <strong>
                          Override {index + 1}: {formatOverrideDate(override.date)}
                        </strong>
                        <button
                          type="button"
                          className="provider-icon-button"
                          onClick={() => removeOverride(override.localId)}
                        >
                          <FaTrash />
                        </button>
                      </div>

                      <div className="provider-two-column provider-override-grid">
                        <label>
                          Date
                          <input
                            type="date"
                            value={override.date}
                            onChange={(event) =>
                              updateOverride(
                                override.localId,
                                "date",
                                event.target.value
                              )
                            }
                          />
                        </label>
                        <label>
                          Reason
                          <input
                            type="text"
                            value={override.reason}
                            onChange={(event) =>
                              updateOverride(
                                override.localId,
                                "reason",
                                event.target.value
                              )
                            }
                            placeholder="Vacation, holiday, extended day..."
                          />
                        </label>
                      </div>

                      <div className="provider-override-toggle-row">
                        <label className="checkbox-row">
                          <input
                            type="checkbox"
                            checked={override.isUnavailableAllDay}
                            onChange={(event) =>
                              updateOverride(
                                override.localId,
                                "isUnavailableAllDay",
                                event.target.checked
                              )
                            }
                          />
                          <span>Unavailable all day</span>
                        </label>
                        <label className="checkbox-row">
                          <input
                            type="checkbox"
                            checked={override.isActive}
                            onChange={(event) =>
                              updateOverride(
                                override.localId,
                                "isActive",
                                event.target.checked
                              )
                            }
                          />
                          <span>Override is active</span>
                        </label>
                      </div>

                      {!override.isUnavailableAllDay && (
                        <>
                          <div className="provider-window-header provider-subsection-header">
                            <strong>Custom windows</strong>
                            <button
                              type="button"
                              className="provider-secondary-button provider-inline-button"
                              onClick={() => addOverrideWindow(override.localId)}
                            >
                              <FaPlus />
                              Add window
                            </button>
                          </div>

                          <div className="provider-window-stack">
                            {override.windows.map((windowEntry, windowIndex) => (
                              <div
                                key={windowEntry.localId}
                                className="provider-window-card provider-nested-window"
                              >
                                <div className="provider-window-header">
                                  <strong>Custom window {windowIndex + 1}</strong>
                                  <button
                                    type="button"
                                    className="provider-icon-button"
                                    onClick={() =>
                                      removeOverrideWindow(
                                        override.localId,
                                        windowEntry.localId
                                      )
                                    }
                                  >
                                    <FaTrash />
                                  </button>
                                </div>

                                <div className="provider-time-row">
                                  <label>
                                    Start
                                    <input
                                      type="time"
                                      value={windowEntry.startTime}
                                      onChange={(event) =>
                                        updateOverrideWindow(
                                          override.localId,
                                          windowEntry.localId,
                                          "startTime",
                                          event.target.value
                                        )
                                      }
                                    />
                                  </label>
                                  <label>
                                    End
                                    <input
                                      type="time"
                                      value={windowEntry.endTime}
                                      onChange={(event) =>
                                        updateOverrideWindow(
                                          override.localId,
                                          windowEntry.localId,
                                          "endTime",
                                          event.target.value
                                        )
                                      }
                                    />
                                  </label>
                                </div>

                                <div className="provider-service-chip-row">
                                  {massageServiceOptions.map((service) => (
                                    <button
                                      key={`${windowEntry.localId}-${service.code}`}
                                      type="button"
                                      className={`provider-service-chip ${
                                        windowEntry.serviceCodes.includes(
                                          service.code
                                        )
                                          ? "selected"
                                          : ""
                                      }`}
                                      onClick={() =>
                                        toggleOverrideWindowService(
                                          override.localId,
                                          windowEntry.localId,
                                          service.code
                                        )
                                      }
                                    >
                                      {service.name}
                                    </button>
                                  ))}
                                </div>
                                <p className="provider-field-note">
                                  No selected services means this override window
                                  supports both massage lengths.
                                </p>
                              </div>
                            ))}
                          </div>
                        </>
                      )}
                    </article>
                  ))}
                </div>
              ) : (
                <p className="provider-empty-state">
                  No date-specific overrides yet. Add one for time off, holidays,
                  or one-off custom hours.
                </p>
              )}

              <button
                type="button"
                className="provider-primary-button"
                onClick={handleOverridesSave}
              >
                Save overrides
              </button>
            </section>

            <section className="provider-card">
              <div className="provider-card-header">
                <div>
                  <h2>Availability preview</h2>
                  <p>
                    Check the actual bookable slots generated from the therapist
                    schedule and Suite #1 availability.
                  </p>
                </div>
              </div>

              <div className="provider-preview-controls">
                <label>
                  Preview date
                  <input
                    type="date"
                    value={previewDate}
                    onChange={(event) => setPreviewDate(event.target.value)}
                  />
                </label>
                <label>
                  Massage service
                  <select
                    value={previewServiceCode}
                    onChange={(event) => setPreviewServiceCode(event.target.value)}
                    disabled={!massageServiceOptions.length}
                  >
                    <option value="">Select a service</option>
                    {massageServiceOptions.map((service) => (
                      <option key={service.code} value={service.code}>
                        {service.name}
                      </option>
                    ))}
                  </select>
                </label>
                <button
                  type="button"
                  className="provider-secondary-button"
                  onClick={handlePreview}
                  disabled={previewLoading}
                >
                  <FaCalendarAlt />
                  {previewLoading ? "Previewing..." : "Preview slots"}
                </button>
              </div>

              {previewResult ? (
                <div className="provider-preview-results">
                  <div className="provider-preview-summary">
                    <div>
                      <span className="provider-summary-label">Therapist</span>
                      <strong>{previewResult.provider?.name || "Unknown"}</strong>
                    </div>
                    <div>
                      <span className="provider-summary-label">Service</span>
                      <strong>
                        {previewResult.serviceOffering?.name || "Unknown"}
                      </strong>
                    </div>
                    <div>
                      <span className="provider-summary-label">Schedule source</span>
                      <strong>
                        {getAvailabilitySourceLabel(
                          previewResult.availabilitySource
                        )}
                      </strong>
                    </div>
                    <div>
                      <span className="provider-summary-label">Suite</span>
                      <strong>
                        {previewResult.suite
                          ? `#${previewResult.suite.number}`
                          : "No massage suite"}
                      </strong>
                    </div>
                  </div>

                  {previewResult.availableSlots?.length ? (
                    <div className="provider-slot-grid">
                      {previewResult.availableSlots.map((slot) => (
                        <div key={slot} className="provider-slot-pill">
                          <FaClock />
                          <span>
                            {new Date(slot).toLocaleTimeString([], {
                              hour: "numeric",
                              minute: "2-digit",
                            })}
                          </span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="provider-empty-state">
                      No bookable massage slots were generated for that date.
                    </p>
                  )}
                </div>
              ) : (
                <p className="provider-empty-state">
                  Choose a date and massage service to preview Suite #1 slots.
                </p>
              )}
            </section>
          </div>
        </div>
      </div>
    </StaffLayout>
  );
};

export default ProviderAvailabilityPage;
